import { BarChart3, RefreshCw } from "lucide-react";
import { Select } from "./Select";

const mapOptions = (values = [], placeholderLabel) =>
  values.map((value) =>
    typeof value === "object"
      ? value
      : { value, label: value || placeholderLabel }
  );

const StandingsView = ({
  standings,
  filters,
  onFilterChange,
  leagues,
  seasons,
  conferences,
  divisions,
  loading,
  onRefresh,
  t
}) => (
  <div className="space-y-6">
    <div className="border border-white/10 rounded-sm overflow-hidden bg-white/[0.02]">
      <div className="p-6 border-b border-white/10 flex items-center justify-between">
        <h2 className="text-2xl font-black uppercase tracking-tight text-white flex items-center gap-3">
          {t("League Standings")}
        </h2>
        {filters.league && filters.season && (
          <span className="text-[11px] font-mono uppercase tracking-widest text-gray-500">
            {filters.league} — {filters.season}
          </span>
        )}
      </div>

      {loading ? (
        <div className="p-12 text-center">
          <RefreshCw className="animate-spin mx-auto mb-4 text-white" size={32} />
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-gray-400">
            {t("Loading standings...")}
          </p>
        </div>
      ) : standings.length === 0 ? (
        <div className="p-12 text-center">
          <BarChart3 className="mx-auto mb-4 text-gray-600" size={36} />
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-gray-500">
            {t("Select a league and season")}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-black/40">
              <tr>
                {["POS", "TEAM", "GP", "W", "L", "PF", "PA", "STREAK"].map(
                  (header) => (
                    <th
                      key={header}
                      className="px-6 py-4 text-left text-[11px] font-mono uppercase tracking-widest text-gray-500"
                    >
                      {t(header)}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {standings.map((team, index) => (
                <tr key={team.team?.id || index} className="hover:bg-white/5 transition-colors">
                  <td className="px-6 py-4 text-white font-black">
                    {team.position || index + 1}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {team.team?.logo && (
                        <img
                          src={team.team.logo}
                          alt={team.team.name}
                          className="w-10 h-10 object-contain border border-white/10 rounded-sm bg-black/40"
                        />
                      )}
                      <div>
                        <p className="font-black text-white uppercase tracking-tight">
                          {team.team?.name || `Team ${index + 1}`}
                        </p>
                        <p className="text-[11px] text-gray-500 uppercase tracking-widest">
                          {team.conference} • {team.division || t("Division")}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center text-white font-semibold">
                    {team.games?.played ?? "-"}
                  </td>
                  <td className="px-6 py-4 text-center text-emerald-300 font-semibold">
                    {team.games?.win ?? "-"}
                  </td>
                  <td className="px-6 py-4 text-center text-red-300 font-semibold">
                    {team.games?.lose ?? "-"}
                  </td>
                  <td className="px-6 py-4 text-center text-white font-semibold">
                    {team.points?.for ?? "-"}
                  </td>
                  <td className="px-6 py-4 text-center text-white font-semibold">
                    {team.points?.against ?? "-"}
                  </td>
                  <td className="px-6 py-4 text-center text-white font-semibold">
                    {team.streak || team.form || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
);

export default StandingsView;
